<template>
  <v-container>
    <v-card elevation="0">
      <v-card-title class="mb-4 px-0">
        <v-btn
          :color="showAddForm ? '' : 'primary'"
          @click="toggleForm"
          elevation="0"
          :prepend-icon="showAddForm ? 'mdi-close' : 'mdi-plus'"
        >
          {{ showAddForm ? 'Отменить' : 'Добавить схему' }}
        </v-btn>
        <v-alert type="info" dense text class="ml-4 mb-0" size="small" v-if="schemes.length >= 2">
          Схемы, которые в списке выше перекрывают действие нижних
        </v-alert>
      </v-card-title>

      <v-expand-transition>
        <div v-if="showAddForm">
          <v-card variant="outlined" class="pa-6 mb-6">
            <h2 class="text-h6 mb-4">
              {{ editingSchemeId ? 'Редактировать схему' : 'Новая схема адаптации' }}
            </h2>
            
            <div>
              <v-row>
                <v-col cols="12" md="3">
                  <v-label class="mb-2">Временной период</v-label>
                  <v-select
                    v-model="newScheme.periodFilterType"
                    :items="[{value: 'all', text: 'Весь период'}, {value: 'custom', text: 'Пользовательский период'}]"
                    variant="outlined"
                    hide-details
                  ></v-select>
                </v-col>
                <template v-if="newScheme.periodFilterType === 'custom'">
                  <v-col cols="12" md="3">
                    <v-label class="mb-2">Начало периода</v-label>
                    <v-text-field
                      v-model.number="newScheme.startDay"
                      :items="timePoints"
                      placeholder="Начало"
                      variant="outlined"
                      :hint="`От 0 до ${timePoints[timePoints.length - 1]}`"
                      :min="0"
                      :max="timePoints[timePoints.length - 1]"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-label class="mb-2">Конец периода</v-label>
                    <v-text-field
                      v-model.number="newScheme.endDay"
                      :items="timePoints"
                      placeholder="Конец"
                      variant="outlined"
                      :hint="`От ${newScheme.startDay} до ${timePoints[timePoints.length - 1]}`"
                      :min="newScheme.startDay"
                      :max="timePoints[timePoints.length - 1]"
                      clearable
                    ></v-text-field>
                  </v-col>
                </template>
              </v-row>
              <div class="mt-2 text-caption text-grey" :class="{'text-grey': selectedTimePoints.length > 0, 'text-error': selectedTimePoints.length === 0}">
                Выбрано временных точек: {{ selectedTimePoints.length }} из {{ timePoints.length }}
              </div>
            </div>  

            <!-- Well Selection -->
            <div>
              <v-row class="mt-4">
                <v-col cols="12" md="3">
                  <v-label class="mb-2">Скважины</v-label>
                  <v-select
                    v-model="newScheme.wellFilterType"
                    :items="[{value: 'all', text: 'Все скважины'}, {value: 'selected', text: 'Выбранные скважины'}]"
                    hide-details
                  ></v-select>
                </v-col>
                <v-expand-transition>
                  <v-col cols="12" md="6" v-if="newScheme.wellFilterType === 'selected'">
                    <v-label class="mb-2">&nbsp;</v-label>
                    <v-select
                      v-model="newScheme.selectedWells"
                      :items="wells"
                      label="Выберите скважины"
                      item-value="id"
                      item-text="name"
                      hide-details
                      multiple
                      chips
                      closeable-chips
                    ></v-select>
                  </v-col>
                </v-expand-transition>
              </v-row>
              <div class="mt-2 text-caption text-grey" :class="{'text-grey': selectedWells.length > 0, 'text-error': selectedWells.length === 0}">
                Выбрано скважин: {{ selectedWells.length }} из {{ wells.length }}
              </div>
            </div>

            <div class="mt-4">
              <v-label class="mb-2">Фильтр по истории</v-label>
              <v-row>
                <v-col v-for="param in ['production', 'debit', 'bhp']" :key="param" cols="12" sm="6" md="3">
                  <label class="text-caption">{{ getParamLabel(param.toLowerCase()) }}</label>
                  <div class="d-flex ga-2">
                    <v-text-field
                      v-model.number="filterInput[param + 'Min']"
                      type="number"
                      placeholder="от"
                      variant="outlined"
                      dense
                      hint="Включительно"
                      preserve-hint
                    ></v-text-field>
                    <v-text-field
                      v-model.number="filterInput[param + 'Max']"
                      type="number"
                      placeholder="до"
                      variant="outlined"
                      dense
                      hint="Включительно"
                      preserve-hint
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
              <div class="mt-2 text-caption text-grey" :class="{'text-grey': newFilteredWells.length > 0, 'text-error': newFilteredWells.length === 0}">
                Подходит под условия: {{ newFilteredWells.length }} из {{ wells.length }}
              </div>
            </div>

            <!-- Adaptation Parameters -->
            <div class="">
                <v-row class="mt-4">
                  <v-col v-for="(enabled, param) in newScheme.adaptationParams" :key="param" cols="12">
                      <v-switch
                          v-model="newScheme.adaptationParams[param]"
                          :label="getAdaptationLabel(param)"
                          hide-details
                          class="pa-0 ma-0"
                      ></v-switch>
                  </v-col>
                </v-row>
            </div>

            <div class="d-flex ga-4 mt-6">
              <v-spacer></v-spacer>
              <v-btn
                @click="handleAddScheme"
                class="mr-4"
                prepend-icon="mdi-content-save"
                color="primary"
              >
                {{ editingSchemeId ? 'Сохранить изменения' : 'Добавить схему' }}
              </v-btn>
              <v-btn
                @click="resetForm"
                prepend-icon="mdi-close"
                elevation="0"
                variant="text"
              >
                Отменить
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-expand-transition>

      <!-- Schemes List -->
      <div>
        <v-alert v-if="schemes.length === 0" type="info" outlined>
          Нет добавленных схем. Нажмите "Добавить схему" для начала.
        </v-alert>


        <draggable v-model="schemes">
          <v-card
            v-for="(scheme, index) in schemes"
            :key="scheme.id"
            class="mb-3"
          >
            <v-card-text class="px-6">
              <div class="d-flex justify-space-between align-start">
                <div class="flex-grow-1">
                  <div class="d-flex flex-wrap align-center ga-4 mb-2">
                    <span class="font-weight-bold">#{{ schemes.length - index }}</span>
                    <v-chip size="small">{{ getPeriodDisplay(scheme) }}</v-chip>
                    <v-chip size="small">{{ getWellsDisplay(scheme) }}</v-chip>
                  </div>
                  <p class="body-2">
                    <span class="font-weight-medium">Адаптация:</span> {{ getAdaptationDisplay(scheme.adaptationParams) }}
                  </p>
                  <div v-if="hasFilter(scheme)" class="text-caption text-grey mt-1">
                    Фильтр: {{ getFilterDisplay(scheme.filterParams) }}
                  </div>
                </div>
                <div class="d-flex ga-1">
                  <v-btn icon size="small" color="primary" @click="handleEditScheme(scheme)"><v-icon>mdi-pencil</v-icon></v-btn>
                  <v-btn icon size="small" color="error" @click="handleDeleteScheme(scheme.id)"><v-icon>mdi-delete</v-icon></v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </draggable>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'AdaptScheme',
  components: {draggable},
  props: ['problem'],
  data() {
    return {
      schemes: [],
      showAddForm: false,
      editingSchemeId: null,
      newScheme: this.getInitialSchemeState(),
      newFilteredWells: [],
      filterInput: {
        productionMin: '',
        productionMax: '',
        debitMin: '',
        debitMax: '',
        bhpMin: '',
        bhpMax: '',
        //pressureDifferenceMin: '',
        //pressureDifferenceMax: ''
      }
    };
  },
  mounted() {
    this.updateFilteredWells();
  },
  watch: {
    filterInput: {
      handler() {
        this.newScheme.filterParams = this.filterParams;
        this.updateFilteredWells();
      },
      deep: true
    },
    wellFilterType: {
      handler() {
        this.updateFilteredWells();
      }
    },
    schemes: {
      handler() {
        this.emitUpdate();
      },
      deep: true
    }
  },
  computed: {
    wells() {
      return this.problem ? this.problem.wells || [] : [];
    },
    timePoints() {
      if (!this.problem || !this.problem.schedule_data) {
        return [];
      }

      return this.problem.schedule_data?.t || [];
    },
    filterParams() {
      return {
        production: {
          min: this.filterInput.productionMin,
          max: this.filterInput.productionMax
        },
        debit: {
          min: this.filterInput.debitMin,
          max: this.filterInput.debitMax
        },
        bhp: {
          min: this.filterInput.bhpMin,
          max: this.filterInput.bhpMax
        },
        //pressureDifference: {
        //  min: this.filterInput.pressureDifferenceMin,
        //  max: this.filterInput.pressureDifferenceMax
        //}
      };
    },
    wellFilterType() {
      return this.newScheme.wellFilterType;
    },
    selectedWells() {
      if (!this.problem) {
        return [];
      }

      if (!this.wells) {
        return [];
      }

      let allWells = this.wells.map(well => well.id);

      if (!this.newScheme.selectedWells) {
        return allWells;
      }

      if (this.newScheme.selectedWells.length === 0) {
        return allWells;
      }

      return this.newScheme.selectedWells;
    },
    selectedTimePoints() {
      if (this.newScheme.periodFilterType === 'all') {
        return this.timePoints;
      }

      return this.timePoints.filter(tp => {
        return (tp >= this.newScheme.startDay || this.newScheme.startDay === '') &&
          (tp <= this.newScheme.endDay || this.newScheme.endDay === '')
      });
    }
  },
  methods: {
    getInitialSchemeState() {
      return {
        periodFilterType: 'all',
        startDay: '',
        endDay: '',
        wellFilterType: 'all',
        selectedWells: [],
        filterEnabled: false,
        filterParams: this.filterParams,
        adaptationParams: {
          production: true,
          debit: true,
          bhp: true,
          pressureDifference: true
        }
      };
    },
    toggleForm() {
        this.showAddForm = !this.showAddForm;
        if (!this.showAddForm) {
            this.resetForm(false); // Не скрываем форму еще раз
        }
    },
    emitUpdate() {
      this.$emit('input', JSON.parse(JSON.stringify(this.schemes)));
    },
    handleAddScheme() {
      // Глубокое копирование, чтобы избежать мутаций
      const schemePayload = JSON.parse(JSON.stringify(this.newScheme));

      const scheme = {
        id: this.editingSchemeId || Date.now(),
        ...schemePayload,
        selectedWells: schemePayload.wellFilterType === 'selected' ? schemePayload.selectedWells : [],
        filterParams: this.filterParams,
      };
      
      if (this.editingSchemeId) {
        const index = this.schemes.findIndex(s => s.id === this.editingSchemeId);
        if (index !== -1) {
            this.schemes.splice(index, 1, scheme);
        }
      }
      else {
        this.schemes.unshift(scheme);
      }
      this.resetForm();
    },
    resetForm(hideForm = true) {
      this.newScheme = this.getInitialSchemeState();
      this.filterInput = {
        productionMin: '',
        productionMax: '',
        debitMin: '',
        debitMax: '',
        bhpMin: '',
        bhpMax: '',
        //pressureDifferenceMin: '',
        //pressureDifferenceMax: ''
      };

      this.updateFilteredWells();
      this.editingSchemeId = null;
      if (hideForm) {
        this.showAddForm = false;
      }
    },
    handleDeleteScheme(id) {
      this.schemes = this.schemes.filter(scheme => scheme.id !== id);
    },
    getValueOrEmpty(value) {
      if (typeof(value) === 'string') {
        return value.length > 0 ? value : '';
      }

      if (typeof(value) === 'number') {
        return value;
      }

      return value || '';
    },
    handleEditScheme(scheme) {
      // Глубокое копирование, чтобы избежать реактивных связей с оригинальным объектом
      const schemeToEdit = JSON.parse(JSON.stringify(scheme));

      this.newScheme = {
        ...this.getInitialSchemeState(), // Начинаем с чистого состояния
        ...schemeToEdit,
        filterEnabled: !!schemeToEdit.filterParams,
      };

      this.filterInput = {
        productionMin: this.getValueOrEmpty(schemeToEdit.filterParams?.production?.min),
        productionMax: this.getValueOrEmpty(schemeToEdit.filterParams?.production?.max),
        debitMin: this.getValueOrEmpty(schemeToEdit.filterParams?.debit?.min),
        debitMax: this.getValueOrEmpty(schemeToEdit.filterParams?.debit?.max),
        bhpMin: this.getValueOrEmpty(schemeToEdit.filterParams?.bhp?.min),
        bhpMax: this.getValueOrEmpty(schemeToEdit.filterParams?.bhp?.max),
        //pressureDifferenceMin: this.getValueOrEmpty(schemeToEdit.filterParams?.pressureDifference?.min),
        //pressureDifferenceMax: this.getValueOrEmpty(schemeToEdit.filterParams?.pressureDifference?.max),
      };

      this.updateFilteredWells();
      this.editingSchemeId = scheme.id;
      this.showAddForm = true;
    },
    updateFilteredWells() {
      if (!this.problem || !this.wells) {
        this.newFilteredWells = [];
        return;
      }

      if (!this.newScheme.filterEnabled) {
        this.newFilteredWells = this.wells;
      }

      const filterParams = this.filterParams;
      let bhpData = this.getFilteredSchedule('bhp_data');
      let productionData = this.getFilteredSchedule('oil_history_data');
      let debitData = this.getFilteredSchedule('schedule_data');

      this.newFilteredWells = this.wells.filter(well => {
        let checks = [];

        if (filterParams.production.min !== '' || filterParams.production.max !== '') {
          const wellProduction = productionData[well.id] || [];
          const filteredDays = wellProduction.filter(production => {
            return (
              (filterParams.production.min === '' || production > parseFloat(filterParams.production.min)) &&
              (filterParams.production.max === '' || production <= parseFloat(filterParams.production.max))
            );
          });

          checks.push(filteredDays.length > 0);
        }
        else {
          checks.push(true);
        }

        if (filterParams.debit.min !== '' || filterParams.debit.max !== '') {
          const wellDebit = debitData[well.id] || [];
          const filteredDays = wellDebit.filter(debit => {
            return (
              (filterParams.debit.min === '' || debit > parseFloat(filterParams.debit.min)) &&
              (filterParams.debit.max === '' || debit <= parseFloat(filterParams.debit.max))
            );
          });

          checks.push(filteredDays.length > 0);
        }
        else {
          checks.push(true);
        }

        if (filterParams.bhp.min !== '' || filterParams.bhp.max !== '') {
          const wellBHP = bhpData[well.id] || [];
          const filteredDays = wellBHP.filter(bhp => {
            return (
              (filterParams.bhp.min === '' || bhp > parseFloat(filterParams.bhp.min)) &&
              (filterParams.bhp.max === '' || bhp <= parseFloat(filterParams.bhp.max))
            );
          });

          checks.push(filteredDays.length > 0);
        }
        else {
          checks.push(true);
        }

        return checks.every(check => check);
      });
    },
    getFilteredSchedule(param) {
      const scheduleData = this.problem[param] || {};
      const allTimePoints = this.problem?.schedule_data?.t || [];
      let filteredData = {};
      for (const wellId of this.selectedWells) {
        const startTime = this.timePoints[0];
        const endTime = this.timePoints[this.timePoints.length - 1];
        const startTimeIndex = allTimePoints.indexOf(startTime);
        const endTimeIndex = allTimePoints.indexOf(endTime);
        let filteredSchedule = scheduleData[wellId] || [];
        filteredSchedule = filteredSchedule.slice(startTimeIndex, endTimeIndex+1);
        filteredData[wellId] = filteredSchedule;
      }

      return filteredData;
    },
    getPeriodDisplay(scheme) {
      if (scheme.periodFilterType === 'all') {
        return 'Весь период';
      }

      if (scheme.startDay && scheme.endDay) {
        return `${scheme.startDay} - ${scheme.endDay}`;
      }

      return 'Период не задан';
    },
    getWellsDisplay(scheme) {
      if (scheme.wellFilterType === 'all') {
        return 'Все скважины';
      }
      
      if (scheme.selectedWells.length === 0) {
        return 'Все скважины';
      }

      if (scheme.selectedWells.length === 1) {
        const well = this.wells.find(w => w.id === scheme.selectedWells[0]);
        return well ? well.name : '1 скважина';
      }
      
      // Правильное склонение для "скважин"
      const n = scheme.selectedWells.length;
      if (n % 10 === 1 && n % 100 !== 11) return `${n} скважина`;
      if ([2,3,4].includes(n % 10) && ![12,13,14].includes(n % 100)) return `${n} скважины`;
      return `${n} скважин`;
    },
    getAdaptationDisplay(params) {
      const activeParams = [];
      if (params.production) {
        activeParams.push('Добыча');
      }

      if (params.debit) {
        activeParams.push('Дебит');
      }

      if (params.bhp) {
        activeParams.push('Забойное давление');
      }

      if (params.pressureDifference) {
        activeParams.push('Разница давлений');
      }
      
      return activeParams.length > 0 ? activeParams.join(', ') : 'Нет';
    },
    getFilterDisplay(params) {
        return Object.keys(params).map(param => {
          const paramFilter = params[param];
          const parts = [];
          if (paramFilter.min !== '') {
            parts.push(`от ${paramFilter.min}`);
          }
          if (paramFilter.max !== '') {
            parts.push(`до ${paramFilter.max}`);
          }
          if (parts.length > 0) {
            return `${this.getParamLabel(param)} (${parts.join(', ')})`;
          }
          return null;
        }).filter(part => part !== null).join('; ');
    },
    hasFilter(scheme) {
      return scheme.filterParams &&
             (scheme.filterParams?.production?.min !== '' || scheme.filterParams?.production?.max !== '' ||
              scheme.filterParams?.debit?.min !== '' || scheme.filterParams?.debit?.max !== '' ||
              scheme.filterParams?.bhp?.min !== '' || scheme.filterParams?.bhp?.max !== ''
              //scheme.filterParams?.pressureDifference?.min !== '' || scheme.filterParams?.pressureDifference?.max !== ''
             );
    },
    getParamLabel(param) {
      const labels = {
        production: 'Добыча',
        debit: 'Дебит',
        bhp: 'Забойное давление',
        pressureDifference: 'Разница давлений'
      };
      return labels[param] || param;
    },
    getAdaptationLabel(param) {
      const labels = {
        production: 'Адаптация по добыче',
        debit: 'Адаптация по дебиту',
        bhp: 'Адаптация по забойному давлению',
        pressureDifference: 'Адаптация по разнице давлений'
      };
      return labels[param] || param;
    }
  }
};
</script>

<style>
.ga-2 {
  gap: 8px;
}

.ga-4 {
  gap: 16px;
}
</style>